import { get, writable } from 'svelte/store';
import { browser } from '$app/environment';
import { User } from '../types/auth.js';
import { tokenToUser, setCookie, deleteCookie } from '../services/auth.js';

/**
 * Store per l'autenticazione dell'utente
 */
function createAuthStore() {
  const { subscribe, set, update } = writable({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  });

  return {
    subscribe,
    
    /**
     * Imposta l'utente autenticato
     * @param {User} user - L'utente autenticato
     * @param {string} token - Il token di accesso
     */
    setUser: (user, token) => {
      if (!browser) return;
      
      // Crea il cookie legacy per compatibilità
      const legacySession = {
        name: btoa(user.name),
        email: btoa(user.email),
        sessionId: btoa(user.sessionId),
        logged: true,
        imgSmall: btoa(user.image),
        userId: user.id,
        veicoliPubblicati: 0,
      };
      
      // Imposta i cookie usando la utility sicura
      setCookie('token', token, { expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString() });
      setCookie('user', JSON.stringify(legacySession), { expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString() });
      
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      // Se le notifiche sono già abilitate (permesso concesso), rigenera e registra
      // il token FCM per il nuovo utente — senza richiedere di riattivare il toggle.
      import('../services/push-notifications.js')
        .then((m) => m.syncOnLoad())
        .catch(() => {});
    },

    /**
     * Imposta lo stato di caricamento
     * @param {boolean} loading - Stato di caricamento
     */
    setLoading: (loading) => {
      update(state => ({
        ...state,
        isLoading: loading
      }));
    },

    /**
     * Imposta un errore
     * @param {string} error - Messaggio di errore
     */
    setError: (error) => {
      update(state => ({
        ...state,
        error,
        isLoading: false
      }));
    },

    /**
     * Pulisce l'errore
     */
    clearError: () => {
      update(state => ({
        ...state,
        error: null
      }));
    },

    /**
     * Effettua il logout
     */
    logout: () => {
      if (!browser) return;

      // NB: il token FCM NON viene cancellato al logout (comportamento come l'app mobile):
      // resta stabile in IndexedDB e viene ri-registrato al prossimo login (vedi setUser/syncOnLoad).
      // La cancellazione esplicita avviene solo dal toggle "off" delle notifiche (disablePush).

      // Rimuove i cookie usando la utility
      deleteCookie('token');
      deleteCookie('user');
      
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    },

    getToken: () => {
      if (!browser) return null;
      return getCookie('token');
    },

    /**
     * Inizializza l'autenticazione dai cookie
     */
    initializeAuth: () => {
      if (!browser) return;
      
      console.log('🔍 Initializing auth from cookies...');
      const allCookies = document.cookie;
      console.log('📋 All cookies:', allCookies);
      
      const tokenCookie = getCookie('token');
      const userCookie = getCookie('user');
      
      console.log('🎫 Token cookie found:', !!tokenCookie);
      console.log('👤 User cookie found:', !!userCookie);

      if (tokenCookie) {
        try {
          const decodedToken = tokenToUser(tokenCookie);
          const user = User.fromDecodedToken(decodedToken);

          if (!user.id) return;
          
          set({ 
            user, 
            isAuthenticated: true, 
            isLoading: false, 
            error: null 
          });

          // Se non esiste il cookie user, crealo
          if (!userCookie) {
            const legacySession = {
              name: btoa(user.name),
              email: btoa(user.email),
              sessionId: btoa(user.sessionId),
              logged: true,
              imgSmall: btoa(user.image),
              userId: user.id,
              veicoliPubblicati: 0,
            };
            
            setCookie('user', JSON.stringify(legacySession), { expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString() });
          }
        } catch (error) {
          console.error('Error initializing auth from token:', error);
          // Se il token non è valido, pulisce i cookie usando la utility
          deleteCookie('token');
          deleteCookie('user');
        }
      } else if (userCookie && !tokenCookie) {
        try {
          // Fallback per sessioni legacy
          const legacySession = JSON.parse(userCookie);
          const user = new User(
            legacySession.userId,
            atob(legacySession.name),
            atob(legacySession.email),
            atob(legacySession.imgSmall),
            '', // Locale non disponibile
            '', // Lastname non disponibile
            atob(legacySession.sessionId),
            {
              documents: false,
              email: false,
              phone: false,
              profile: false,
              terms: false,
            }
          );

          set({ 
            user, 
            isAuthenticated: true, 
            isLoading: false, 
            error: null 
          });
        } catch (error) {
          console.error('Error initializing auth from legacy cookie:', error);
          // Pulisce il cookie corrotto
          document.cookie = 'user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
      }
    }
  };
}


/**
 * Utility per leggere i cookie
 * @param {string} name - Nome del cookie
 * @returns {string|null} - Valore del cookie o null se non trovato
 */
function getCookie(name) {
  if (!browser) return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

export const authStore = createAuthStore();

// Initialize auth store when the module loads
if (browser) {
  console.log('🔐 Initializing auth store on module load...');
  authStore.initializeAuth();
}